import * as Sentry from '@sentry/electron/renderer'
import { useCallback, useEffect, useState } from 'react'
import TimeAgo from 'react-timeago'
import { HatGlasses } from 'lucide-react'
import { Menu, Tooltip } from '@highlight/ui'
import { incognitoTimeRemainingCompactFormatter } from '@renderer/utils/incognito-time-remaining'
import { useSetMiniContentHovered, useSetMiniHovered, useSetMiniOpenSurface } from '../../../atoms'

const INCOGNITO_ON_CLASS = 'text-success-subtle/75 hover:bg-success/15 hover:text-success-subtle'
const INCOGNITO_MENU_SURFACE_ID = 'incognito-menu'

interface IncognitoChipProps {
  incognitoTimer?: number
  onDisable: () => Promise<void>
}

export function IncognitoChip({ incognitoTimer, onDisable }: IncognitoChipProps) {
  const setOpenSurface = useSetMiniOpenSurface()
  const setMiniHovered = useSetMiniHovered()
  const setContentHovered = useSetMiniContentHovered()
  const [open, setOpen] = useState(false)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      setOpenSurface({ id: INCOGNITO_MENU_SURFACE_ID, open: nextOpen })
      if (nextOpen) {
        setMiniHovered(true)
        setContentHovered(true)
      }
    },
    [setOpenSurface, setMiniHovered, setContentHovered],
  )

  useEffect(() => {
    return () => {
      setOpenSurface({ id: INCOGNITO_MENU_SURFACE_ID, open: false })
    }
  }, [setOpenSurface])

  const handleDisable = useCallback(async () => {
    try {
      await onDisable()
    } catch (error) {
      Sentry.captureException(error, { tags: { module: 'mini-incognito-chip' } })
    }
  }, [onDisable])

  return (
    <Menu open={open} onOpenChange={handleOpenChange}>
      <div onClick={(event) => event.stopPropagation()}>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Menu.Trigger
                variant="ghost"
                size="md"
                shape="square"
                aria-label="Incognito mode (on)"
                className={INCOGNITO_ON_CLASS}
              />
            }
          >
            <HatGlasses aria-hidden="true" className="size-4" />
          </Tooltip.Trigger>
          <Tooltip.Content sideOffset={14}>Incognito mode (on)</Tooltip.Content>
        </Tooltip>
      </div>
      <Menu.Content
        side="bottom"
        align="start"
        sideOffset={12}
        positionerClassName="z-30 after:absolute after:-inset-2 after:-z-10 after:content-['']"
      >
        <Menu.Item onClick={handleDisable}>
          <span>Turn off</span>
          {incognitoTimer && (
            <span className="text-foreground-placeholder ml-auto text-xs">
              <TimeAgo
                date={incognitoTimer}
                formatter={incognitoTimeRemainingCompactFormatter}
                minPeriod={60}
                live={true}
              />
            </span>
          )}
        </Menu.Item>
      </Menu.Content>
    </Menu>
  )
}
